import {Message} from 'com/gongxigames/math/core/game/message/Message';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {ISettings, SettingsParametersSchema} from '@gongxigames/framework/core';
import {
    AutoplayEvents,
    GameClientWrapperSettingsParameters,
    LogicEvents,
    SlotSettingsParameters,
    Xhr
} from '@gongxigames/framework/slot';
import {RubyPlayGameClientWrapperSettingsParameters} from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapperSettings';
import {RubyPlayGameClientWrapper} from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapper';
import {ResponseBody} from '@gongxigames/framework/integrations/rubyplay/types';
import {GameServerError} from '@gongxigames/framework/integrations/rubyplay/RubyPlayErrorsHandler';
import {SessionTimerData, UserAction} from '@gongxigames/framework/integrations/pop/types';
import {SessionTimer} from '@gongxigames/framework/integrations/pop/sessionTimer/SessionTimer';
import {SingleSetPromotion, IPromotionLogicInterface} from '@gongxigames/framework/promotions';

const EXPIRED_SESSION_CODE = 'PA-5';
const SESSION_TIMER_ERROR_CODE = 'GS-97';
const SESSION_TIMER_PATH = '/pop/sessionTimer';

type POPSettingsParameters = {
    crossLaunchUrl: string;
};

export const POP_SETTINGS_PARAMETERS_SCHEMA: SettingsParametersSchema<POPSettingsParameters> = {
    crossLaunchUrl: {
        readonly: true,
        defaultValue: '',
        urlParser: {
            alias: 'cross_launch_url',
            typePropertyInURL: 'string'
        }
    }
};

type POPSettings = ISettings<
    SlotSettingsParameters &
    GameClientWrapperSettingsParameters &
    RubyPlayGameClientWrapperSettingsParameters &
    POPSettingsParameters
>;

export class POPGameClientWrapper extends RubyPlayGameClientWrapper {
    public static NAME = 'pop';
    protected _storedRequest!: Message;
    protected _crossLaunchUrl!: string;
    protected _resetSessionTimerUrl!: string;
    protected _tokenPlayerSession!: string;
    protected _promotionLogicInterface: IPromotionLogicInterface = new SingleSetPromotion();
    protected initParameters(): void {
        super.initParameters();

        const settings = this.getSettings<POPSettings>();

        settings.addParametersSchema(POP_SETTINGS_PARAMETERS_SCHEMA);

        this._crossLaunchUrl = settings.get('crossLaunchUrl');
        this._resetSessionTimerUrl = settings.get('serverUrl') + SESSION_TIMER_PATH;
        this._tokenPlayerSession = settings.get('playerSession');
    }

    protected onInitSessionResponseError(body: ResponseBody): void {
        if (this.isSessionTokenExpired(body.errorCode)) {
            this.requestToCrossLaunchURL();
        } else {
            this._errors.handleInitSessionError(body);
            this.clearPingBalanceTimeout();
        }
    }

    protected isSessionTokenExpired(errorCode?: string): boolean {
        return errorCode === EXPIRED_SESSION_CODE && this._crossLaunchUrl !== undefined;
    }

    protected handlePromotion(body: ResponseBody, action: Action): void {
        if (this.isInitAction(action)) {
            this.getPromotion().init({
                initResponse: body,
                clientWrapper: this,
                defaultBet: this._defaultPromotionBet
            });
        } else {
            this.getPromotion().update(body);
        }
    }

    protected updateBalance(body: ResponseBody): void {
        const balance = Number(body.data.player.balance);

        this.getSession().getPlayer().setBalance(balance);

        this.getPromotion().update(body);

        this._eventManager.emit(LogicEvents.BALANCE_UPDATED, balance);
    }

    protected requestToCrossLaunchURL(): void {
        Xhr.get(this._crossLaunchUrl, (body: ResponseBody) => {
            this.clearConnectionTimeout();

            const {status, redirectUrl} = body;

            if (status === 'success' && redirectUrl) {
                this.redirect(redirectUrl);
            } else {
                this._errors.handleInitSessionError(body);
                this.clearPingBalanceTimeout();
            }
        }, null, null, this.onErrorRequest.bind(this));
    }

    protected onGameServerActionError(body: GameServerError, request: Message): void {
        const {code, message} = body.data;

        if (code === SESSION_TIMER_ERROR_CODE) {
            this.stopAutoPlay();
            this.showSessionTimerPopup(message.data);
            this.storeLastRequestToServer(request);
        } else {
            this._errors.handleGameServerError(body);
        }

        this.clearPingBalanceTimeout();
    }

    protected showSessionTimerPopup(data: SessionTimerData): void {
        SessionTimer.showPopup({
            onChoiceAction: this.updateSessionTimer.bind(this),
            ...data
        });
    }

    protected storeLastRequestToServer(request: Message): void {
        this._storedRequest = request;
    }

    protected updateSessionTimer(userAction: UserAction, interval?: number): void {
        const data = {
            userAction,
            interval,
            sessionToken: this._tokenPlayerSession
        };

        Xhr.post(this._resetSessionTimerUrl, JSON.stringify(data), (body: ResponseBody) => {
            this.clearConnectionTimeout();

            if (body.status === 'ok') {
                this.applyAction(userAction);
            } else {
                this.onGameServerActionError(body, this._storedRequest);
            }
        }, {'content-type': 'application/json'}, this.onErrorRequest.bind(this));
    }

    protected applyAction(userAction: UserAction): void {
        if (this.isContinuePlaying(userAction)) {
            this.gameServerActionRequest(this._storedRequest);
            this.pingBalance();
        } else {
            this.quitGame();
        }
    }

    protected redirectToLogout(): void {
        this.requestToLogoutUrl(this.redirect.bind(this));
    }

    protected requestToLogoutUrl(callback: Function): void {
        Xhr.get(this.getSettings<POPSettings>().get('logoutUrl')!, (body: ResponseBody) => {
            this.clearConnectionTimeout();

            const {status, redirectUrl} = body;

            if (status === 'success' && redirectUrl) {
                callback(redirectUrl);
            } else {
                this._errors.handleRequestError(body as any, false);
                this.clearPingBalanceTimeout();
            }
        }, null, null, this.onErrorRequest.bind(this));
    }

    protected isContinuePlaying(userAction: UserAction): boolean {
        return userAction === UserAction.RESET_TIMER;
    }

    protected stopAutoPlay(): void {
        this._eventManager.emit(AutoplayEvents.FORCE_STOP_AUTOPLAY);
    }

    protected onErrorRequest(xhr?: XMLHttpRequest, isTimeout?: boolean): void {
        this.clearConnectionTimeout();
        this.clearPingBalanceTimeout();
        this._errors.handleRequestError(xhr!, isTimeout!);
    }

    protected addIgnoreCacheParameterToRequest(): boolean {
        return super.addIgnoreCacheParameterToRequest() && !this.getPromotion().isActive();
    }
}
